import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../button/Button';
import { Spinner } from '../spinner/Spinner';
import {
    getErrorMessage,
    getErrorStatus,
    isBoxDownloadUrl,
    MAX_UPLOAD_BYTES,
    parseRecordFile,
    RecordFile,
} from '../../services/boxFilesService';
import { deleteFile, getDownloadUrl, RecordFilesSource, sourceKey, uploadFile } from './recordFilesSource';
import { useRecordFiles } from './useRecordFiles';
import { RecordFileRow } from './RecordFileRow';
import { RecordFileUpload } from './RecordFileUpload';
import styles from './RecordFiles.module.scss';

export interface RecordFileName {
    name: string;
}

export interface RecordFilesProps {
    source: RecordFilesSource;
    /** Shows a file input. Only valid for `kind: 'record'` sources. */
    allowUpload?: boolean;
    /** Shows a Delete button for each file the server marks `deletable`. Only valid for `kind: 'record'` sources. */
    allowDelete?: boolean;
    /** Called once per upload batch with the names of the files the server accepted. */
    onUpload?: (files: RecordFileName[]) => void;
    /** Called after a file is deleted. */
    onDelete?: (files: RecordFileName[]) => void;
    /** Called with true while any upload or delete is in flight, and false once all have settled. */
    onBusyChange?: (busy: boolean) => void;
    /** Change to refetch the list, e.g. after another panel for the same record uploaded or deleted. */
    refreshToken?: number | string;
    /** Accessible name of the file list; give each list on a page a distinct one. */
    listLabel?: string;
}

type Feedback = { tone: 'status' | 'error'; message: string } | null;

/** Where focus should go once the next render settles. */
type FocusRequest = { kind: 'row'; fileId: string } | { kind: 'container' } | null;

export const RecordFiles = ({
    source,
    allowUpload = false,
    allowDelete = false,
    onUpload,
    onDelete,
    onBusyChange,
    refreshToken,
    listLabel,
}: RecordFilesProps) => {
    const { t, i18n } = useTranslation();
    const { state, reload, addFile, removeFile } = useRecordFiles(source, refreshToken);
    const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
    const [deletingCount, setDeletingCount] = useState(0);
    const [uploading, setUploading] = useState(false);
    // Two slots so a delete mid-batch doesn't overwrite "Uploading…" (and vice versa).
    const [uploadFeedback, setUploadFeedback] = useState<Feedback>(null);
    // Bumped per upload batch; keys the upload alert so a repeated identical error is announced again.
    const [uploadAttempt, setUploadAttempt] = useState(0);
    const [actionFeedback, setActionFeedback] = useState<Feedback>(null);

    const isRecord = source.kind === 'record';
    const canUpload = allowUpload && isRecord;
    const canDelete = allowDelete && isRecord;

    // Results of an operation started for one source must not land on another, or after unmount.
    const key = sourceKey(source);
    const keyRef = useRef(key);
    keyRef.current = key;
    const sourceRef = useRef(source);
    sourceRef.current = source;
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);
    const isCurrent = useCallback((startedFor: string) => mountedRef.current && startedFor === keyRef.current, []);

    const filesRef = useRef<RecordFile[]>([]);
    filesRef.current = state.status === 'ready' ? state.files : [];

    // Focus targets.
    const rowRefs = useRef(new Map<string, HTMLLIElement>());
    const listRef = useRef<HTMLUListElement>(null);
    const emptyRef = useRef<HTMLParagraphElement>(null);
    const loadErrorRef = useRef<HTMLParagraphElement>(null);
    const focusRequest = useRef<FocusRequest>(null);

    const setRowRef = useCallback((fileId: string, element: HTMLLIElement | null) => {
        if (element) rowRefs.current.set(fileId, element); else rowRefs.current.delete(fileId);
    }, []);

    useEffect(() => {
        const request = focusRequest.current;
        if (!request || state.status === 'loading') return;
        focusRequest.current = null;
        // A container request follows a control that unmounted (Retry, the upload input, the last row);
        // honour it only if focus was lost with it, never pull it back from elsewhere on the page.
        const active = document.activeElement;
        if (request.kind === 'container' && active && active !== document.body) return;
        const row = request.kind === 'row' ? rowRefs.current.get(request.fileId) : undefined;
        // The first button in a row is its Download button.
        const target: HTMLElement | null | undefined = row?.querySelector('button')
            ?? listRef.current ?? emptyRef.current ?? loadErrorRef.current;
        target?.focus();
    }, [state]);

    // Report busy transitions; release the parent if we unmount while busy.
    const busy = uploading || deletingCount > 0;
    const onBusyChangeRef = useRef(onBusyChange);
    onBusyChangeRef.current = onBusyChange;
    const reportedBusy = useRef(false);
    useEffect(() => {
        if (reportedBusy.current === busy) return;
        reportedBusy.current = busy;
        onBusyChangeRef.current?.(busy);
    }, [busy]);
    useEffect(() => () => {
        if (reportedBusy.current) onBusyChangeRef.current?.(false);
    }, []);

    useEffect(() => {
        setUploadFeedback(null);
        setActionFeedback(null);
        setPendingIds(new Set());
        setDeletingCount(0);
        setUploading(false);
    }, [key]);

    const setPending = useCallback((id: string, pending: boolean) => {
        setPendingIds(prev => {
            const next = new Set(prev);
            if (pending) next.add(id); else next.delete(id);
            return next;
        });
    }, []);

    const handleDownload = useCallback(async (file: RecordFile) => {
        const startedFor = keyRef.current;
        setPending(file.id, true);
        setActionFeedback(null);
        try {
            const { url } = await getDownloadUrl(sourceRef.current, file.id);
            if (!isCurrent(startedFor)) return;
            if (!isBoxDownloadUrl(url)) throw new Error('Unexpected download URL');
            // Box serves these with Content-Disposition: attachment, so the page stays put. A same-tab
            // navigation isn't subject to popup blocking the way window.open after an await is.
            window.location.assign(url);
        } catch {
            if (!isCurrent(startedFor)) return;
            setActionFeedback({
                tone: 'error',
                message: t('RecordFiles.DownloadFailed', { defaultValue: 'We couldn\'t download {{name}}. Please try again.', name: file.name }),
            });
        } finally {
            if (isCurrent(startedFor)) setPending(file.id, false);
        }
    }, [isCurrent, setPending, t]);

    const handleDelete = useCallback(async (file: RecordFile) => {
        const confirmed = window.confirm(
            t('RecordFiles.ConfirmDelete', { defaultValue: 'Delete {{name}}? This cannot be undone.', name: file.name })
        );
        if (!confirmed) return;
        const startedFor = keyRef.current;
        setPending(file.id, true);
        setDeletingCount(c => c + 1);
        setActionFeedback(null);
        try {
            await deleteFile(sourceRef.current, file.id);
            if (!isCurrent(startedFor)) return;

            // Only move focus if it was in the removed row (or already lost); never steal it from elsewhere.
            const active = document.activeElement;
            const row = rowRefs.current.get(file.id);
            if (!active || active === document.body || row?.contains(active)) {
                const files = filesRef.current;
                const index = files.findIndex(f => f.id === file.id);
                const neighbour = index >= 0 ? files[index + 1] ?? files[index - 1] : undefined;
                focusRequest.current = neighbour ? { kind: 'row', fileId: neighbour.id } : { kind: 'container' };
            }

            removeFile(file.id);
            setActionFeedback({ tone: 'status', message: t('RecordFiles.Deleted', { defaultValue: 'Deleted {{name}}.', name: file.name }) });
            onDelete?.([{ name: file.name }]);
        } catch (err) {
            if (!isCurrent(startedFor)) return;
            const status = getErrorStatus(err);
            const serverMessage = status === 404 || status === 409 ? getErrorMessage(err) : undefined;
            setActionFeedback({
                tone: 'error',
                message: serverMessage
                    ? `${file.name}: ${serverMessage}`
                    : t('RecordFiles.DeleteFailed', { defaultValue: 'We couldn\'t delete {{name}}. Please try again.', name: file.name }),
            });
        } finally {
            if (isCurrent(startedFor)) {
                setPending(file.id, false);
                setDeletingCount(c => Math.max(0, c - 1));
            }
        }
    }, [isCurrent, onDelete, removeFile, setPending, t]);

    const handleUpload = useCallback(async (files: File[]) => {
        const startedFor = keyRef.current;
        const errors: string[] = [];
        const accepted: RecordFileName[] = [];
        let needsReload = false;

        const sendable = files.filter(file => {
            if (file.size > MAX_UPLOAD_BYTES) {
                errors.push(t('RecordFiles.TooLarge', { defaultValue: '{{name}} is larger than 50 MB and was not uploaded.', name: file.name }));
                return false;
            }
            return true;
        });

        setUploadAttempt(n => n + 1);
        setUploadFeedback(sendable.length ? { tone: 'status', message: t('RecordFiles.Uploading', 'Uploading…') } : null);
        if (sendable.length) setUploading(true);

        for (const file of sendable) {
            try {
                const created = parseRecordFile(await uploadFile(sourceRef.current, file));
                // Stop after the current file if the source changed or we unmounted.
                if (!isCurrent(startedFor)) return;
                if (created) {
                    addFile(created);
                    accepted.push({ name: created.name });
                } else {
                    // The server accepted it but the body is unusable; refetch the list instead of guessing.
                    needsReload = true;
                    accepted.push({ name: file.name });
                }
            } catch (err) {
                if (!isCurrent(startedFor)) return;
                const status = getErrorStatus(err);
                const serverMessage = status === 409 || status === 413 ? getErrorMessage(err) : undefined;
                errors.push(
                    serverMessage
                        ? `${file.name}: ${serverMessage}`
                        : status === 413
                            ? t('RecordFiles.TooLarge', { defaultValue: '{{name}} is larger than 50 MB and was not uploaded.', name: file.name })
                            : t('RecordFiles.UploadFailed', { defaultValue: 'We couldn\'t upload {{name}}. Please try again.', name: file.name })
                );
            }
        }

        if (!isCurrent(startedFor)) return;
        setUploading(false);
        if (needsReload) {
            // The reload replaces the upload input while it loads, so land focus on the refreshed list.
            focusRequest.current = { kind: 'container' };
            reload();
        }
        if (accepted.length) onUpload?.(accepted);

        if (errors.length) {
            setUploadFeedback({ tone: 'error', message: errors.join(' ') });
        } else if (accepted.length) {
            setUploadFeedback({
                tone: 'status',
                message: t('RecordFiles.Uploaded', { defaultValue: 'Uploaded {{names}}.', names: accepted.map(f => f.name).join(', ') }),
            });
        }
    }, [addFile, isCurrent, onUpload, reload, t]);

    const handleRetry = useCallback(() => {
        focusRequest.current = { kind: 'container' };
        reload();
    }, [reload]);

    return (
        <div className={styles.recordFiles}>
            {state.status === 'loading' && <Spinner isLoading />}

            {state.status === 'error' && (
                <div className={styles.loadError}>
                    <p role="alert" tabIndex={-1} ref={loadErrorRef}>
                        {t('RecordFiles.LoadFailed', 'We couldn\'t load files. Please try again.')}
                    </p>
                    <Button color="blue" type="button" onClick={handleRetry}>
                        {t('RecordFiles.Retry', 'Retry')}
                    </Button>
                </div>
            )}

            {state.status === 'ready' && (
                state.files.length === 0
                    ? (
                        <p className={styles.empty} tabIndex={-1} ref={emptyRef}>
                            {t('RecordFiles.Empty', 'No files uploaded yet')}
                        </p>
                    )
                    : (
                        <ul
                            className={styles.list}
                            aria-label={listLabel ?? t('RecordFiles.ListLabel', 'Files')}
                            tabIndex={-1}
                            ref={listRef}
                        >
                            {state.files.map(file => (
                                <RecordFileRow
                                    key={file.id}
                                    file={file}
                                    locale={i18n?.language}
                                    pending={pendingIds.has(file.id)}
                                    onDownload={handleDownload}
                                    onDelete={canDelete && file.deletable ? handleDelete : undefined}
                                    rowRef={setRowRef}
                                />
                            ))}
                        </ul>
                    )
            )}

            <p className={styles.feedback} role="status" aria-live="polite">
                {actionFeedback?.tone === 'status' ? actionFeedback.message : ''}
            </p>
            {actionFeedback?.tone === 'error' && (
                <p className={styles.feedbackError} role="alert">{actionFeedback.message}</p>
            )}

            {canUpload && state.status === 'ready' && (
                <RecordFileUpload uploading={uploading} onFiles={handleUpload} />
            )}

            {canUpload && (
                <p className={styles.feedback} role="status" aria-live="polite">
                    {uploadFeedback?.tone === 'status' ? uploadFeedback.message : ''}
                </p>
            )}
            {uploadFeedback?.tone === 'error' && (
                <p key={uploadAttempt} className={styles.feedbackError} role="alert">{uploadFeedback.message}</p>
            )}
        </div>
    );
};
